/* global chrome */
import { Controller } from "@hotwired/stimulus";

// Drives the VRBO staleness banner on the Quotes page. Talks to the
// BookingFence Chrome extension over externally_connectable
// (chrome.runtime.sendMessage with the pinned extension id — the browser
// only exposes this channel to origins listed in the extension manifest).
//
// The server renders the stale/fresh decision; this controller only manages
// live extension state: leave the manual-instructions fallback visible when
// the extension can't be reached, otherwise swap in the Refresh now button,
// reflect an already-running refresh, and poll for progress until done.
export default class extends Controller {
  static values = { extensionId: String };
  static targets = ["fallback", "ready", "button", "status"];

  connect() {
    this.pollTimer = null;
    this.finished = false;
    if (!this.extensionApiAvailable()) return; // fallback stays visible
    this.pingWithRetry();
    // A single ping on connect made any transient miss permanent: reloading the
    // extension, or catching its MV3 service worker asleep, left the banner in
    // its not-installed fallback until the page was manually reloaded. Re-ping
    // when the tab comes back to the foreground so it self-corrects.
    this.onVisible = () => {
      if (document.visibilityState === "visible" && this.readyTarget.hidden) {
        this.pingWithRetry();
      }
    };
    document.addEventListener("visibilitychange", this.onVisible);
  }

  disconnect() {
    this.stopPolling();
    if (this.onVisible) {
      document.removeEventListener("visibilitychange", this.onVisible);
    }
  }

  // Waking an idle MV3 service worker takes a moment, and a reloading extension
  // is unreachable for a beat. Retry a couple of times before concluding it
  // isn't there.
  async pingWithRetry(attempts = 3, delayMs = 700) {
    for (let i = 0; i < attempts; i++) {
      const reached = await this.ping();
      if (reached) return true;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs));
    }
    return false;
  }

  extensionApiAvailable() {
    return (
      typeof chrome !== "undefined" &&
      !!chrome.runtime &&
      typeof chrome.runtime.sendMessage === "function" &&
      !!this.extensionIdValue
    );
  }

  // Promise wrapper that resolves null on any failure (extension missing,
  // wrong id, no listener) instead of throwing.
  send(message) {
    return new Promise((resolve) => {
      try {
        chrome.runtime.sendMessage(
          this.extensionIdValue,
          message,
          (response) => {
            if (chrome.runtime.lastError || response === undefined)
              resolve(null);
            else resolve(response);
          },
        );
      } catch {
        resolve(null);
      }
    });
  }

  // Resolves true when the extension answered, so callers can retry on a miss.
  async ping() {
    const status = await this.send({ action: "bfStatus" });
    if (!status) return false; // not installed (or not reachable yet) — fallback stays

    this.fallbackTarget.hidden = true;
    this.readyTarget.hidden = false;

    if (status.running) {
      this.showRefreshing();
      this.startPolling();
    } else if (status.lastRun?.state === "login_required") {
      this.setStatus(
        "You're logged out of vrbo.com — log in there, then click Refresh now.",
      );
    } else if (!status.configured) {
      this.setStatus(
        "The extension has no credentials for this site — set them in its popup first.",
      );
    }
    return true;
  }

  async refresh() {
    this.finished = false;
    this.showRefreshing();
    this.startPolling();
    // bfRefresh resolves when the run completes (or immediately for the quick
    // outcomes: skipped, login_required, idle, error). Polling covers progress
    // display and the case where this response never arrives.
    const result = await this.send({ action: "bfRefresh" });
    this.finish(result);
  }

  finish(result) {
    if (this.finished) return;
    this.finished = true;
    this.stopPolling();
    this.enableButton();

    switch (result?.state) {
      case "done":
        this.setStatus("Refreshed ✓ — reloading…");
        this.reload();
        break;
      case "idle":
        this.setStatus("Nothing needed refreshing — reloading…");
        this.reload();
        break;
      case "login_required":
        this.setStatus(
          "You're logged out of vrbo.com — log in there, then try again.",
        );
        break;
      case "skipped":
        this.setStatus(
          "Another refresh is already running — this banner will update when it finishes.",
        );
        this.finished = false;
        this.startPolling();
        break;
      case "not_configured":
        this.setStatus(
          "The extension has no credentials for this site — set them in its popup first.",
        );
        break;
      case "error":
        this.setStatus(
          `Refresh failed: ${result.error || "see the extension popup for details"}`,
        );
        break;
      default:
        this.setStatus("Couldn't reach the extension — is it enabled?");
    }
  }

  startPolling() {
    this.stopPolling();
    this.pollTimer = setInterval(() => this.poll(), 2000);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async poll() {
    const status = await this.send({ action: "bfStatus" });
    if (!status) return;

    if (status.running) {
      this.setStatus(`Refreshing… ${this.progressText(status.lastRun)}`);
    } else {
      this.finish(
        status.lastRun
          ? { state: status.lastRun.state, error: status.lastRun.error }
          : null,
      );
    }
  }

  progressText(lastRun) {
    const buckets = Object.values(lastRun?.byProperty || {});
    if (buckets.length === 0) return "";
    const done = buckets.reduce((sum, b) => sum + (b.done || 0), 0);
    const total = buckets.reduce((sum, b) => sum + (b.total || 0), 0);
    return total > 0 ? `${done}/${total} done` : "";
  }

  showRefreshing() {
    this.buttonTarget.disabled = true;
    this.buttonTarget.textContent = "Refreshing…";
    this.setStatus("Refreshing…");
  }

  enableButton() {
    this.buttonTarget.disabled = false;
    this.buttonTarget.textContent = "Refresh now";
  }

  setStatus(text) {
    this.statusTarget.textContent = text;
  }

  reload() {
    setTimeout(() => {
      if (window.Turbo)
        window.Turbo.visit(window.location.href, { action: "replace" });
      else window.location.reload();
    }, 800);
  }
}
